
import {throwError as observableThrowError, of as observableOf,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Location } from '../models/location.model';
import { CountryLocationHash } from '../models/location-country-hash.interface';
import { Url } from '../models/url/url.model';
import { UrlParam } from '../models/url/url-param.model';
import { UrlParams } from '../models/url/url-params.model';
import { Log } from '../utils/log';
import { SessionService } from './session.service';
import { RegionalLocations } from '../models/regional-locations.model';
import { RegionLocationHash } from '../models/region-location-hash.interface';
import { LocationFilterEvent } from '../models/location-filter-event.model';
import { ReferenceService } from './reference.service';
import { Utils } from '../utils/utils';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable()
export class LocationService {

    private _countryLocationHashMap: CountryLocationHash = {};

    constructor(private http: HttpClient, private session: SessionService, private refService: ReferenceService) {}

    /**
     * getAllLocations
     * @param state (optional)
     * @param temporary (optional)
     * @param inviteOnly (optional)
     * @param operational (optional)
     * @param service (optional) name of the service to filter locaitons by
     */
    public getAllLocations(state?:string, temporary?:boolean, inviteOnly?:boolean, operational?:boolean,
            service?:string, accessCode?: string): Observable<Location[]> {

        let params:UrlParams = new UrlParams();

        if(state) {
            let stateParam:UrlParam = new UrlParam('state', state);
            params.add(stateParam);
        }
        if(temporary !== null && temporary !== undefined) {
            let temporaryParam:UrlParam = new UrlParam('temporary', temporary);
            params.add(temporaryParam);
        }
        if(inviteOnly !== null && inviteOnly !== undefined) {
            let inviteOnlyParam:UrlParam = new UrlParam('inviteOnly', inviteOnly);
            params.add(inviteOnlyParam);
        }
        if(operational !== null && operational !== undefined) {
            let operationalParam:UrlParam = new UrlParam('operational', operational);
            params.add(operationalParam);
        }
        if(service) {
            params.add(new UrlParam('serviceName', service));
        }
        if(accessCode) {
            params.add(new UrlParam('accessCode', accessCode));
        }

        let url:Url;

        if (environment.useStaticAPIData) {
            if (inviteOnly) {
                url = new Url('getAccessCodeLocations.json');
                return observableOf(null);
            } else {
                url = new Url('getAllLocations.json');
            }
        } else {
            url = new Url('locations/', params);
        }

        // Initially token may not be available
        const options = {
            headers: new HttpHeaders({
            'Authorization': this.session.token || ''
            })
        };

        return this.http.get(url.toString(), options).pipe(
                map(res => {
                    let returnObj: Location[] = new Array<Location>();
                    let resJson:any = res;
                    resJson.forEach((location: any) => {
                        returnObj.push(new Location(location));
                    });

                    // only display locations that have an effective date
                    // effective date must be set and be in the past
                    if (!this.session.effectiveDate) {
                        returnObj = returnObj.filter((loc) => {
                            if (loc.effectiveDate === undefined) {
                                return false;
                            } else {
                                if (Utils.CompareDates(loc.effectiveDate, new Date()) >= 0) {
                                    return true;
                                } else {
                                    return false;
                                }
                            }
                        });
                    }

                    if (returnObj.length > 0) {
                        returnObj.forEach(location => {
                            location.state = this.refService.getStatenameByCountryAndStateISO(location.countryCode, location.state);
                            location.countryName = this.refService.getCountryByIsoCode(location.countryCode).officialName;
                        });
                        this.hashLocations(returnObj);
                        return returnObj;
                    } else {
                        return null;
                    }
                }, catchError(this.handleError))
        );
    }

    /**
     * Returns an Array of locations based on filter
     * @param {LocationFilterEvent} event the filter params from the location filter event
     * @returns {Location[]}
     */
    public getFilteredLocations(event: LocationFilterEvent): Observable<Location[]> {
        let params: UrlParams = new UrlParams();

        if (event.numberOfAppointments !== null) {
            params.add(new UrlParam('minimum', event.numberOfAppointments));
        } else {
            params.add(new UrlParam('minimum', 1));
        }

        if (event.on) {
            params.add(new UrlParam('filterTimestampBy', 'on'));
            params.add(new UrlParam('timestamp', Utils.getShortISOYearMonthDay(event.filterDate)));
        } else if (event.by) {
            params.add(new UrlParam('filterTimestampBy', 'before'));
            params.add(new UrlParam('timestamp', Utils.getShortISOYearMonthDay(Utils.getFutureDay(event.filterDate, 1))));
        } else {
            params.add(new UrlParam('limit', 5));
        }

        if (this.session.service !== null && this.session.service !== undefined) {
            params.add(new UrlParam('serviceName', this.session.service));
        }

        let url: Url;

        if (environment.useStaticAPIData) {
            url = new Url('getAllLocations.json');
        } else {
            url = new Url('slots/asLocations', params);
        }

        // Initially token may not be available
        const options = {
            headers: new HttpHeaders({
            'Authorization': this.session.token || ''
            })
        };

        return this.http.get(url.toString(), options).pipe(
                map(res => {
                    let returnObj: Location[] = new Array<Location>();
                    let resJson: any = res;
                    resJson.forEach((location: any) => {
                        returnObj.push(new Location(location));
                    });

                    // only display locations that have an effective date
                    // effective date must be set and be in the past
                    if (!this.session.effectiveDate) {
                        returnObj = returnObj.filter((loc) => {
                            if (loc.effectiveDate === undefined) {
                                return false;
                            } else {
                                if (Utils.CompareDates(loc.effectiveDate, new Date()) >= 0) {
                                    return true;
                                } else {
                                    return false;
                                }
                            }
                        });
                    }

                    if (returnObj.length > 0) {
                        returnObj.forEach(location => {
                            location.state = this.refService.getStatenameByCountryAndStateISO(location.countryCode, location.state);
                            location.countryName = this.refService.getCountryByIsoCode(location.countryCode).officialName;
                        });
                        this.hashLocations(returnObj);
                        return returnObj;
                    } else {
                        return null;
                    }
                }, catchError(this.handleError))
        );
    }

    /**
     * Returns an Array of locations for the United States sorted by State and City
     */
    public getUSLocations(): Array<Array<Location>> {
        return this.getLocationsByCountry('US');
    }

    /**
     * Returns an Array of locations for the United States sorted by region state and city
     */
    public getUsLocationsByRegion(): Array<RegionalLocations> {
        // hash map the locations by region
        let regions: RegionLocationHash = {};
        let usLocations = this.getLocationsByCountry('US');
        if (usLocations) {
            usLocations.forEach(stateLocation => {
                switch (stateLocation[0].state) {
                    case 'WA':
                    case 'OR':
                    case 'NV':
                    case 'AK':
                    case 'HI':
                    case 'CA':
                    case 'WASHINGTON':
                    case 'ALASKA':
                    case 'CALIFORNIA':
                    case 'HAWAII':
                    case 'NEVADA':
                    case 'OREGON':
                        if (!regions['Far West']) {
                            regions['Far West'] = new Array<Array<Location>>();
                        }
                        regions['Far West'].push(stateLocation);
                        break;
                    case 'MT':
                    case 'ID':
                    case 'WY':
                    case 'UT':
                    case 'CO':
                    case 'COLORADO':
                    case 'IDAHO':
                    case 'MONTANA':
                    case 'UTAH':
                    case 'WYOMING':
                        if (!regions['Rocky Mountain']) {
                            regions['Rocky Mountain'] = new Array<Array<Location>>();
                        }
                        regions['Rocky Mountain'].push(stateLocation);
                        break;
                    case 'ND':
                    case 'MN':
                    case 'SD':
                    case 'NE':
                    case 'IA':
                    case 'KS':
                    case 'MO':
                    case 'IOWA':
                    case 'KANSAS':
                    case 'MINNESOTA':
                    case 'MISSOURI':
                    case 'NORTH DAKOTA':
                    case 'NEBRASKA':
                    case 'SOUTH DAKOTA':
                        if (!regions['Plains']) {
                            regions['Plains'] = new Array<Array<Location>>();
                        }
                        regions['Plains'].push(stateLocation);
                        break;
                    case 'AZ':
                    case 'NM':
                    case 'TX':
                    case 'OK':
                    case 'ARIZONA':
                    case 'NEW MEXICO':
                    case 'OKLAHOMA':
                    case 'TEXAS':
                        if (!regions['Southwest']) {
                            regions['Southwest'] = new Array<Array<Location>>();
                        }
                        regions['Southwest'].push(stateLocation);
                        break;
                    case 'WI':
                    case 'IL':
                    case 'IN':
                    case 'OH':
                    case 'MI':
                    case 'ILLINOIS':
                    case 'INDIANA':
                    case 'MICHIGAN':
                    case 'OHIO':
                    case 'WISCONSIN':
                        if (!regions['Great Lakes']) {
                            regions['Great Lakes'] = new Array<Array<Location>>();
                        }
                        regions['Great Lakes'].push(stateLocation);
                        break;
                    case 'AR':
                    case 'LA':
                    case 'MS':
                    case 'TN':
                    case 'KY':
                    case 'WV':
                    case 'VA':
                    case 'NC':
                    case 'SC':
                    case 'AL':
                    case 'GA':
                    case 'FL':
                    case 'ALABAMA':
                    case 'ARKANSAS':
                    case 'FLORIDA':
                    case 'GEORGIA':
                    case 'KENTUCKY':
                    case 'LOUISIANA':
                    case 'MISSISSIPPI':
                    case 'NORTH CAROLINA':
                    case 'SOUTH CAROLINA':
                    case 'TENNESSEE':
                    case 'VIRGINIA':
                    case 'WEST VIRGINIA':
                        if (!regions['Southeast']) {
                            regions['Southeast'] = new Array<Array<Location>>();
                        }
                        regions['Southeast'].push(stateLocation);
                        break;
                    case 'NY':
                    case 'PA':
                    case 'NJ':
                    case 'MD':
                    case 'DE':
                    case 'DC':
                    case 'DISTRICT OF COLUMBIA':
                    case 'DELAWARE':
                    case 'MARYLAND':
                    case 'NEW JERSEY':
                    case 'NEW YORK':
                    case 'PENNSYLVANIA':
                        if (!regions['Mideast']) {
                            regions['Mideast'] = new Array<Array<Location>>();
                        }
                        regions['Mideast'].push(stateLocation);
                        break;
                    case 'ME':
                    case 'VT':
                    case 'NH':
                    case 'RI':
                    case 'CT':
                    case 'CONNECTICUT':
                    case 'MAINE':
                    case 'NEW HAMPSHIRE':
                    case 'RHODE ISLAND':
                    case 'VERMONT':
                        if (!regions['New England']) {
                            regions['New England'] = new Array<Array<Location>>();
                        }
                        regions['New England'].push(stateLocation);
                        break;
                    default:
                        if (!regions['Other']) {
                            regions['Other'] = new Array<Array<Location>>();
                        }
                        regions['Other'].push(stateLocation);
                        break;
                }
            });
        }
        let returnObj = new Array<RegionalLocations>();
        for (let key in regions) {
            let regionLocations = new RegionalLocations();
            regionLocations.region = key;
            regionLocations.locations = regions[key];
            returnObj.push(regionLocations);
        }
        return returnObj;
    }

    /**
     * Returns an Array of locations for Canada sorted by Province and City
     */
    public getCALocations(): Array<Array<Location>> {
        return this.getLocationsByCountry('CA');
    }

    /**
     * Returns an Array of an Array of locations sorted by country, state, city
     * that are not the United States and Canada
     */
    public getOtherCountryLocations(): Array<Array<Array<Location>>> {
        let returnLocations: Array<Array<Array<Location>>> = new Array<Array<Array<Location>>>();
        let keys: Array<string> = new Array<string>();
        for(let key in this._countryLocationHashMap) {
            if (key !== 'US' && key !== 'CA') {
                keys.push(key);
            }
        }

        // keys.sort((a, b) => {
        //     if(a === b) {
        //         return 0;
        //     } else if (a > b) {
        //         return 1;
        //     } else {
        //         return -1;
        //     }
        // });

        keys.forEach(key => {
            returnLocations.push(this.getLocationsByCountry(key));
        });

        return returnLocations;
    }

    public getLocation(locationId: number): Observable<Location> {

        // Initially token may not be available
        const options = {
            headers: new HttpHeaders({
            'Authorization': this.session.token || ''
            })
        };

        let url:Url;

        if (environment.useStaticAPIData) {
            url = new Url('getLocation.json');
        } else {
            url = new Url('locations/' + locationId);
        }

        return this.http.get(url.toString(), options).pipe(
                    map(res => {
                        if(res) {
                            let returnLocation = new Location(res);
                            returnLocation.state =
                                this.refService.getStatenameByCountryAndStateISO(returnLocation.countryCode, returnLocation.state);
                            returnLocation.countryName = this.refService.getCountryByIsoCode(returnLocation.countryCode).officialName;
                            return returnLocation;
                        } else {
                            Log.error('Error occured with getting location. API did not return a location.');
                            return null;
                        }
                    }, catchError(this.handleError))
        );
    }


    private getLocationsByCountry(countryCode: string): Array<Array<Location>> {
        if (this._countryLocationHashMap[countryCode]) {
            let returnLocations: Array<Array<Location>> = new Array<Array<Location>>();
            let usLocations = this._countryLocationHashMap[countryCode];
            for(let key in usLocations) {
                let locations = usLocations[key];
                returnLocations.push(locations);
            }
            // sort by state
            this.sortLocationsByState(returnLocations);
            return returnLocations;
        }
        return null;
    }

    private sortLocationsByState(locations: Array<Array<Location>>) {
        locations.sort((locationSetA, locationSetB) => {
            if (locationSetA[0].state.toLocaleLowerCase() === locationSetB[0].state.toLocaleLowerCase()) {
                return 0;
            } else if (locationSetA[0].state.toLocaleLowerCase() > locationSetB[0].state.toLocaleLowerCase()) {
                return 1;
            } else {
                return -1;
            }
        });
    }

    private hashLocations(locations: Location[]) {
        this._countryLocationHashMap = {};

        locations.sort((a, b) => {
            if (a.countryName.toLocaleLowerCase() === b.countryName.toLocaleLowerCase()) {
                return 0;
            } else if (a.countryName.toLocaleLowerCase() > b.countryName.toLocaleLowerCase()) {
                return 1;
            } else {
                return -1;
            }
        });

        locations.forEach(location => {
            if (location.countryCode !== '') {
                if (!this._countryLocationHashMap[location.countryCode]) {
                    this._countryLocationHashMap[location.countryCode] = {};
                }
                if (this._countryLocationHashMap[location.countryCode][location.state]) {
                    this._countryLocationHashMap[location.countryCode][location.state].push(location);
                } else {
                    this._countryLocationHashMap[location.countryCode][location.state] = new Array<Location>();
                    this._countryLocationHashMap[location.countryCode][location.state].push(location);
                }
            }
        });

        // sort the location city arrays
        for (let key1 in this._countryLocationHashMap) {
            let countryLocations = this._countryLocationHashMap[key1];
            for (let key2 in countryLocations) {
                let locs = countryLocations[key2];
                locs.sort((locationA, locationB) => {
                    if (locationA.city.toLocaleLowerCase() === locationB.city.toLocaleLowerCase()) {
                        return 0;
                    } else if (locationA.city.toLocaleLowerCase() > locationB.city.toLocaleLowerCase()) {
                        return 1;
                    } else {
                        return -1;
                    }
                });
            }
        }
    }

    private handleError(error: any) {
        let errMsg = '';
        if (error.message) {
            errMsg = error.message;
        } else if (error.status) {
            if (error.statusText) {
                errMsg = 'Server error';
                //console.error(error.statusText);
            } else {
                // this is an logic error we need to formulate the message here
            }
        } else {
            errMsg = 'Server error';
        }

        return observableThrowError(errMsg);
    }

}
